import { UserRole, UserStatus } from "@/types";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useListContext } from "react-admin";
import { z } from "zod";
import { VettingUsersListFilter } from "../../types";
import { UsersFilterProps } from "./vetting-users-filter.types";

const initialFormState = {
  roles: [],
  statuses: [],
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formSchema = z.object({
  roles: z.array(z.nativeEnum(UserRole)),
  statuses: z.array(z.nativeEnum(UserStatus)),
});

type FormData = z.infer<typeof formSchema>;

const UsersFilter: FC<UsersFilterProps> = ({ onModalClose }) => {
  const [filtersFormData, setFiltersFormData] = useState<Partial<FormData>>({});

  const { filterValues, setFilters } = useListContext();

  const formData: FormData = {
    roles:
      filtersFormData.roles ?? filterValues.roles ?? initialFormState.roles,
    statuses:
      filtersFormData.statuses ??
      filterValues.statuses ??
      initialFormState.statuses,
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: "roles" | "statuses"
  ) => {
    const checked = event.target.checked;
    const fieldValue = event.target.name as UserRole | UserStatus;

    let currentArray = formData[field] ? [...formData[field]!] : [];

    if (checked) {
      if (!currentArray.includes(fieldValue)) {
        currentArray.push(fieldValue);
      }
    } else {
      currentArray = currentArray.filter((v) => v !== fieldValue);
    }

    setFiltersFormData({ ...formData, [field]: currentArray });
  };

  const onSubmit = () => {
    const filters: Pick<VettingUsersListFilter, "roles" | "statuses"> = {
      roles: formData.roles.length ? formData.roles : undefined,
      statuses: formData.statuses.length
        ? (formData.statuses as VettingUsersListFilter["statuses"])
        : undefined,
    };

    setFilters({ ...filterValues, ...filters });
    onModalClose();
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Role
        </Typography>
        <FormControlLabel
          name={UserRole.MUSE}
          label={"Woman"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.roles.includes(UserRole.MUSE)}
              onChange={(e) => handleCheckboxChange(e, "roles")}
            />
          }
        />
        <FormControlLabel
          name={UserRole.ALPHA}
          label={"Man"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.roles.includes(UserRole.ALPHA)}
              onChange={(e) => handleCheckboxChange(e, "roles")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Status
        </Typography>
        <FormControlLabel
          name={UserStatus.PENDING}
          label={"Pending"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.statuses?.includes(UserStatus.PENDING)}
              onChange={(e) => handleCheckboxChange(e, "statuses")}
            />
          }
        />
        <FormControlLabel
          name={UserStatus.REJECTED}
          label={"Rejected"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.statuses?.includes(UserStatus.REJECTED)}
              onChange={(e) => handleCheckboxChange(e, "statuses")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setFiltersFormData(initialFormState);
          }}
        >
          Reset
        </Button>

        <Stack direction={"row"} spacing={2}>
          <Button variant="outlined" onClick={onModalClose}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Filter
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export { UsersFilter };
